<script>
	const urlUtilities = require('utilities/urlUtilities');
	const stringUtilities = require('utilities/stringUtilities');
	const metadataUtils = require('metadata-utils');
	const metadata = require('core/metadata');
	const utilities = require('utilities');
	const articleUtilities = require('utilities/articleUtilities');

	module.exports = {
		// name is required to call recursively
		// https://v1.vuejs.org/guide/components.html#Recursive-Component
		name: 'article-result-content',
		props: {
			article: {
				required: true
			},
			onProductLinkClick: {
				type: Function,
				default() {}
			},
			subArticle: {
				type: Boolean,
				default: false
			},
			showSummary: {
				type: Boolean,
				default: true
			},
			productLink: {
				type: String,
				default: ''
			},
			debug: {
				default: false
			}
		},
		data() {
			return {
				featureIconMap: new Map([
					[
						'ell',
						{
							className: 'feature-ell',
							componentName: 'ell-icon',
							label: 'Collections for ELLs'
						}
					],
					[
						'challenge_texts',
						{
							className: 'feature-challenge',
							componentName: 'lightning-icon',
							label: 'Challenge Articles'
						}
					],
					[
						'support_texts',
						{
							className: 'feature-boost',
							componentName: 'apple-icon',
							label: 'Boost Articles'
						}
					],
					[
						'ebook',
						{
							className: 'feature-ebook',
							componentName: 'ebook-icon',
							label: 'eBook'
						}
					],
					[
						'stepreads',
						{
							className: 'feature-stepreads',
							componentName: 'step-icon',
							label: 'StepRead'
						}
					],
					[
						'decodable_teacher_editions',
						{
							className: 'feature-decodables',
							componentName: 'decodables-icon',
							label: 'Decodables'
						}
					]
				])
			};
		},
		components: {
			'article-image': require('components/article-image.vue').default,
			'ell-icon': require('components/svg/ell-icon.vue').default,
			'lightning-icon': require('components/svg/lightning-icon.vue').default,
			'apple-icon': require('components/svg/apple-icon.vue').default,
			'ebook-icon': require('components/svg/ebook-icon.vue').default,
			'step-icon': require('components/svg/step-icon.vue').default,
			'decodables-icon': require('components/svg/decodables-icon.vue').default,
			'overflow-text': require('components/overflow-text.vue').default
		},
		computed: {
			noLexile() {
				return !this.article.lexileCode && !this.article.minLexile && !this.article.maxLexile;
			},
			anyReadWorksArticles() {
				let foundReadWorks = false;
				(this.article.specialCollections || []).forEach(c => {
					if (c.id === metadataUtils.READWORKS_COLLECTION_NODE_ID) {
						foundReadWorks = true;
					}
				});
				return foundReadWorks;
			},
			articleClass() {
				let clazz = 'article';
				let productType = this.article.productType;
				if (this.subArticle) {
					clazz += ' sub-article';
				} else {
					clazz += ' ' + productType;
				}
				return clazz;
			},
			isPairedText() {
				if (!this.article) {
					return false;
				}
				return this.article.productType === 'P';
			},
			isAAD() {
				if (!this.article) {
					return false;
				}
				return this.article.productType === 'AAD';
			},
			isBookStudy() {
				const article = this.article;

				return !!(article && article.productType === 'TS' && article.subType === 7000);
			},
			isBookStudyChild() {
				const article = this.article;

				return !!(article && article.textSet && article.textSet.subType === 7000);
			},
			decodableLSFoci() {
				const article = this.article;
				if (!(article && article.dteGroups)) {
					return null;
				}

				const decodableLetterSounds = metadata.decodableLetterSounds;
				const letterSoundFoci = new Set();

				for (const group of article.dteGroups) {
					Object.values(group.articles)
						.flatMap(a => a.decodableTeacherEdition.letterSoundFoci)
						.forEach(letterSoundFoci.add, letterSoundFoci);
				}

				const text = [...letterSoundFoci].map(focus => decodableLetterSounds[focus] || focus).join(', ');
				const label = letterSoundFoci.size === 1 ? 'Letter-Sound Focus' : 'Letter-Sound Foci';

				return {
					text,
					label
				};
			},
			isArticleWithVideo() {
				let article = this.article;
				let hasVideo = false;
				if (article.productType === 'A' && article.hasVideo) {
					hasVideo = true;
				}
				return hasVideo;
			},
			summary() {
				if (!this.article || !this.showSummary) {
					return '';
				}
				return this.article.highlight || this.article.excerpt;
			},
			contentSize() {
				return this.isPairedText ? 'small-12' : 'small-9';
			},
			productUrl() {
				if (!this.article || this.subArticle) {
					return null;
				}
				if (this.productLink) {
					return this.productLink;
				}
				return urlUtilities.getProductURL(this.article);
			},
			qsetTotals() {
				let article = this.article;
				let qsTotals = article.qsTotals;
				let qsetTotals = '';

				if (qsTotals && qsTotals.qs >= 0) {
					qsetTotals = qsTotals.qs + ' Q set' + (qsTotals.qs > 1 ? 's' : '');
				}
				return qsetTotals;
			},
			debugInfo() {
				let article = this.article;
				let pid = '';
				let features = [];

				if (this.debug && article) {
					features = article.features || [];
					if (article.au) {
						features.unshift('au');
					}
					if (article.sr) {
						features.push('sr');
					}
					if (article.p) {
						pid = article.p + '<br />';
					}
				}

				return pid + features.join(', ');
			},
			wordCount() {
				let count = '';
				let article = this.article;
				if (this.isPairedText && article.hasVideo) {
					count += article.articleList[0].wordCount;
					count += ' Words & ';
					count += utilities.formatTime(article.articleList[1].video.duration);
					count += ' Minutes';
				} else if (this.isPairedText) {
					article.articleList.forEach((a, idx) => {
						if (idx > 0) {
							count += ' & ';
						}
						count += a.wordCount;
					});
				} else {
					count = stringUtilities.formatNumber(article.wordCount);
				}

				if (!this.isPairedText || !article.hasVideo) {
					count += ' Words';
				}

				return count;
			},
			pairedVideoGrades() {
				let grades = [];
				let article = this.article;
				if (this.isPairedText && article.hasVideo) {
					grades = article.articleList[0].grades;
				}
				return grades;
			},
			iconList() {
				if (this.isPairedText) {
					return [];
				}

				const featureIconMap = this.featureIconMap;
				const { features = [], sr } = this.article;

				let list = features.reduce((featureList, featureName) => {
					if (featureIconMap.has(featureName)) {
						featureList.push(featureIconMap.get(featureName));
					}

					return featureList;
				}, []);

				if (sr) {
					list.push(featureIconMap.get('stepreads'));
				}

				return list;
			},
			bookStudyTitle() {
				const { bookStudy, textSet, title } = this.article;
				let category;
				let bookTitle;
				let articleTitle = title;

				if (this.isBookStudyChild) {
					const book = metadata.books.find(b => b.id === textSet.book) || {};

					bookTitle = book.title || '';
					category = textSet.category;
					articleTitle = textSet.title;
				} else {
					bookTitle = bookStudy.book.title;
					category = bookStudy.category;
				}

				const categoryString = articleUtilities.getBookStudyCategoryText(category);

				return `${bookTitle} - ${categoryString}: ${articleTitle}`;
			},
			articleTitle() {
				if (this.isBookStudy) {
					return this.bookStudyTitle;
				}

				return this.article.title;
			}
		}
	};
</script>
<style lang="scss">
	@import 'mixins/flexbox';
	@import 'config/colors';
	a.article.article-result-content {
		@include flexbox();
		margin: 0rem 0rem 0.5rem 0rem !important; // border: 1px solid transparent;
		// border-radius: 5px;
		min-height: 7rem;
		color: $c-black;
		&:hover {
			cursor: pointer; // border: 1px solid $c-grey;
			// box-shadow: 1px 1px 3px $c-grey;
			.article-header .title-container h2 {
				color: $c-black;
			}
		}

		&.sub-article {
			border: none; // Paired text
			box-shadow: none !important;
			.article-header .title-container h2 {
				font-size: 1.25rem;
				color: $c-black;
			}
		}
		.article-header {
			@include flexbox();
			.title-container {
				@include flex(1);

				h2 {
					display: inline;
					font-size: 1.25rem;
					color: $c-blue;
				}
				.paired-text-article-list {
					margin: 0;
					padding: 0 1rem;
					font-size: 0.875rem;
					font-weight: 500;
				}
			}
			h3 {
				margin-top: 0.25rem;
			}
		}
		.article-content {
			@include flex(1);
			padding: 0rem 0.5rem;
		}
		.pill {
			display: inline-block;
			vertical-align: text-top;
			padding: 0 0.5rem;
			line-height: 1.5;
			border-radius: 2px;
			font-size: 0.75rem;
			float: none;
			margin-left: 0;

			&.article-a-day {
				color: $c-pill-aad;
				background-color: $c-pill-aad-bg;
			}

			&.paired-text {
				color: $c-pill-paired-text;
				background-color: $c-pill-paired-text-bg;
			}

			&.book-studies {
				color: $c-pill-book-studies;
				background-color: $c-pill-book-studies-bg;
			}
		}

		.article-meta {
			color: $c-slate-muted;
			font-size: 0.875em;

			ul {
				display: flex;
				align-items: center;
				flex-wrap: wrap;
			}

			.feature-decodables {
				white-space: normal;
				display: inline-flex;
				flex: 1 1;

				button {
					color: $c-slate-muted;
					margin-right: 0.25rem;
				}
			}
		}

		.feature-icon {
			svg {
				stroke: $c-slate-muted;
				height: 1.375rem;
				width: 1.375rem;
			}

			.ell-icon,
			.decodables-icon {
				fill: $c-slate-dark;
			}

			.apple-icon {
				width: 1.1rem;
			}
		}

		figure.article-special-collection-image img {
			max-width: 100%;
			max-height: 2rem;
		}
	}
</style>
<template>
	<a
		:class="[articleClass, { '-has-featured-image': article.img, '-has-summary': !!summary }, 'article-result-content']"
		:href="productUrl"
		@click="onProductLinkClick(article)">
		<article-image
			v-if="article.img"
			:variant="article.productType === 'TS' && article.subType === 7000 ? 'contain' : 'cover'"
			:alt-text="article.imgAlt || article.img_alt"
			:image-url="article.img">
		</article-image>
		<div class="article-content">
			<header class="article-header">
				<div class="title-container">
					<div class="title-content">
						<h2>{{ articleTitle }}</h2>
						<div
							v-if="isPairedText"
							class="paired-text pill">
							{{ article.hasVideo ? 'Paired Video' : article.subType === 8001 ? 'Spanish-English Paired Text' : 'Paired Text' }}
						</div>
						<div
							v-else-if="isAAD"
							class="article-a-day pill">
							Article-A-Day
						</div>
						<div
							v-else-if="isBookStudy"
							class="book-studies pill">
							Book Study
						</div>
						<button
							v-else-if="isBookStudyChild"
							v-tooltip="bookStudyTitle"
							class="button -link book-studies pill">
							Book Study
						</button>
						<div
							v-if="debug"
							class="debug-info"
							contenteditable="true"
							@click.prevent.stop
							v-html="debugInfo"></div>
					</div>
					<ol
						v-if="isPairedText"
						class="paired-text-article-list">
						<li
							v-for="text in article.articleList"
							:key="text.title">
							{{ text.title }} {{ text.video && text.subType === 103 ? '(Video)' : '' }}
						</li>
					</ol>
					<h3>
						{{ article.domainString }}
					</h3>
				</div>
				<slot name="title-tools"></slot>
			</header>
			<footer class="article-meta">
				<ul>
					<li
						v-if="article.grades && article.grades.length"
						class="grade">
						{{ article.grades | getGradeRangeWithOrdinal }}
					</li>
					<li
						v-if="noLexile"
						class="lexile">
						<template v-if="isAAD || isBookStudy || anyReadWorksArticles"> Lexile Pending </template>
						<template v-else> no Lexile </template>
					</li>
					<li
						v-else-if="!article.minLexile && article.lexileCode"
						class="lexile">
						{{ Array.isArray(article.lexileCode) ? article.lexileCode.join(' - ') : article.lexileCode }}
					</li>
					<li
						v-else-if="article.minLexile && isPairedText && !article.hasVideo"
						class="lexile">
						<span v-if="article.subType === 8000">{{ article.minLexile }}L & {{ article.maxLexile }}L</span>
						<span v-else>{{ article.minLexile }}L</span>
					</li>
					<li
						v-else-if="article.minLexile && isPairedText && article.hasVideo"
						class="lexile">
						N/A
					</li>
					<li
						v-else-if="article.minLexile && (isAAD || isBookStudy)"
						class="lexile">
						{{ article.minLexile }}L to {{ article.maxLexile }}L
					</li>
					<li
						v-if="article.wordCount"
						class="word-count">
						{{ wordCount }}
					</li>
					<li class="text-type">{{ article.textTypeString }}</li>
					<li
						class="text-type"
						v-if="isArticleWithVideo">
						Video
					</li>
					<li
						v-if="!isAAD && !subArticle && !isBookStudy && qsetTotals"
						class="qset-totals">
						{{ qsetTotals }}
					</li>
					<li
						v-for="icon in iconList"
						:key="icon.label"
						class="feature-icon"
						:class="icon.className">
						<button
							class="button -link"
							v-tooltip="icon.label">
							<component :is="icon.componentName"></component>
						</button>
						<overflow-text
							v-if="icon.componentName === 'decodables-icon' && decodableLSFoci"
							class="decodable-ls-foci"
							:text="decodableLSFoci.text"
							:label="decodableLSFoci.label"></overflow-text>
					</li>
				</ul>
				<template v-for="sc in article.specialCollections">
					<figure
						v-if="sc.imageUrlM"
						class="article-special-collection-image">
						<img v-bind:src="sc.imageUrlM" />
					</figure>
					<h3
						v-if="!sc.imgUrl && sc.name"
						class="article-special-collection-name">
						{{ sc.name }}
					</h3>
				</template>
			</footer>
			<slot name="footer-tools"></slot>
			<div
				v-if="summary"
				class="article-summary">
				<p v-html="summary"></p>
			</div>
		</div>
	</a>
</template>
